/**
 * @Author: qinyifang
 * @Date: 2025-04-28 15:35:13
 * @Description: 封装Axios请求库，提供HTTP请求和响应拦截处理
 */
import axios from "axios"
import type { AxiosError } from "axios"
import { get as getProperty } from "@jlpay/utils"
import { router } from "@/router"
import { ElMessageBox, ElMessage } from "element-plus"
import type { IOnDataMoreInfo, IOnData, IOnCompleted, IOnError, CustomAxiosRequestConfig } from "@/types/app"
import { useUserStore } from "@/store/user"
import { buildSobotLoginUrl } from "@/assets/js/utils/utils"

// API前缀
export const API_PREFIX = import.meta.env.VITE_APP_PREFIX

// 请求超时时间
const TIME_OUT = 100000

// 内容类型
export const ContentType = {
  json: "application/json",
  stream: "text/event-stream",
  download: "application/octet-stream"
}

// 基础axios配置
axios.defaults.timeout = TIME_OUT
axios.defaults.headers["Content-Type"] = ContentType.json
axios.defaults.headers["X-Requested-With"] = "XMLHttpRequest"
axios.defaults.withCredentials = true

// 基础请求选项
const baseOptions = {
  method: "GET",
  mode: "cors",
  credentials: "include",
  headers: new Headers({
    "Content-Type": ContentType.json
  })
}

// 创建axios实例
export const apiClient = axios.create({
  baseURL: API_PREFIX,
  timeout: TIME_OUT,
  headers: {
    "Content-Type": ContentType.json
  },
  withCredentials: true
})

// 获取当前平台标识
function getCurrentPlatform(): string {
  const userStore = useUserStore()
  return userStore.currentPlatform || "ai"
}

// 添加请求拦截器，支持自定义配置
apiClient.interceptors.request.use(
  (config) => {
    const userStore = useUserStore()
    // 根据当前平台获取对应的 token
    const platform = getCurrentPlatform()
    config.headers["Platform"] = platform
    const loginInfo = userStore.getLoginInfoByPlatform()
    const token = loginInfo?.token
    // 默认拼 jl_ai 前缀；调用方显式传入 Authorization 时不覆盖
    if (token && !config.headers?.["Authorization"]) {
      config.headers = config.headers || {}
      config.headers["Authorization"] = `jl_ai:${token}`
    }

    // 添加自定义配置，用于控制是否显示错误提示
    const customConfig = config as CustomAxiosRequestConfig
    customConfig.showError = customConfig.showError !== false // 默认显示错误，除非明确设置为false

    return config
  },
  (error) => Promise.reject(error)
)

// 登录过期统一处理
function handleLoginExpired(msg?: string) {
  const userStore = useUserStore()
  const platform = getCurrentPlatform()

  // 智齿坐席页（/sobot-summary，平台复用 ai）：token 失效须带 target 回跳本页，且只清 ai 桶，
  // 不能走下方通用 ai 分支（localStorage.clear 会连带清掉其他平台登录态与智齿会话草稿，/login 丢 target）
  if (router.currentRoute.value.path === "/sobot-summary") {
    // 防死循环：30 秒内新签发的 token 仍报失效，说明后端拒绝该 token
    //（如网关未放行 ai 平台鉴权），再跳登录重登只会拿到同样的 token 无限循环 ——
    // 保留登录态原地报错，暴露真实失败原因
    const loginAt = Number(userStore.getLoginInfoByPlatform("ai")?.loginAt) || 0
    if (Date.now() - loginAt < 30 * 1000) {
      ElMessage.error(`登录态校验失败：${msg || "token 被服务端拒绝，请稍后重试或联系管理员"}`)
      return
    }
    // 真正的过期：仅清本页登录态（不做全局 localStorage.clear），整页跳登录页重新登录后回跳
    ElMessage.error(msg || "登录已过期，请重新登录")
    userStore.setLoginInfoByPlatform("ai", null)
    window.location.replace(buildSobotLoginUrl())
    return
  }

  // 其余平台：清除 Pinia store 的内存状态（必须在 localStorage.clear 之前，否则路由守卫会检测到登录状态）
  ElMessage.error(msg || "登录已过期，请重新登录")
  userStore.setLoginInfoByPlatform(platform, null)
  // 清除所有存储数据
  localStorage.clear()
  sessionStorage.clear()
  // 如果当前平台是 "ai"，跳转到登录页；否则保持在当前页面
  if (platform === "ai" || platform === "agentic") {
    router.replace("/login")
  } else if (platform === "manage" || platform.startsWith("agent_")) {
    // iframe 嵌入场景（管理端 manage / POS agent_*）：向父级传递登录失效信息，由父窗口重注入 token
    if (window.parent && window.parent !== window) {
      window.parent.postMessage(
        {
          type: "UNAUTHORIZED",
          message: msg || "登录已过期",
          platform: platform
        },
        "*"
      )
    }
  }
}

// axios响应拦截器 - 处理数据包装和错误处理
apiClient.interceptors.response.use(
  (response) => {
    const data = response.data
    const config = response.config as CustomAxiosRequestConfig

    // 业务状态码判断
    if (data && data.ret_code) {
      // 处理登录过期或未登录（这些错误总是要处理的）
      if (
        data.ret_code === "A0230" ||
        data.ret_code === "A0400" ||
        data.ret_code === "A0401" ||
        data.ret_code === "E1"
      ) {
        handleLoginExpired(data.ret_msg)
        return Promise.reject(data.ret_msg)
      }

      // 根据配置决定是否显示业务错误
      if (data.ret_code !== "00000" && data.ret_code !== "00") {
        if (config.showError !== false) {
          ElMessage.error(data.ret_msg)
        }
        return Promise.reject(data.ret_msg)
      }
    }

    if (data && data.data) {
      if (Array.isArray(data.data)) {
        // 如果 data.data 是数组，说明是分页或列表数据，返回整个 data
        return data
      } else if (typeof data.data === "object" && data.data !== null) {
        // 如果 data.data 是对象，返回 data.data
        return data.data
      }
    }

    // 默认回退逻辑
    return data
  },
  (error: AxiosError) => {
    console.error("API 错误:", error)
    const status = getProperty(error, "response.status")
    const message = getProperty(error, "message")
    const config = error.config as CustomAxiosRequestConfig

    // 根据配置决定是否显示HTTP错误
    if (config.showError !== false) {
      // 处理HTTP错误
      if (status === 404) {
        ElMessageBox.alert("未能找到您所查找的资源。它可能已被移除或暂时不可用。", "提示")
      } else if (status === 500) {
        ElMessageBox.alert("抱歉给您带来不便了！我们的系统现在遇到了一点小麻烦。我们会尽快修复，请稍后再尝试。", "提示")
      } else {
        // 网络错误提示
        let msg = "请求超时，请稍后再试"
        if (!error.response) {
          msg = "网络异常，请检查您的网络连接"
        } else if (message && typeof message === "string" && message.toLowerCase() === "network error") {
          msg = "网络请求失败，请检查你的网络"
        }
        ElMessageBox.alert(msg, "提示")
      }
    }

    return Promise.reject(error.response)
  }
)

// 处理流式响应
const handleStream = (response: Response, onData: IOnData, onCompleted?: IOnCompleted, onError?: IOnError) => {
  if (!response.ok) throw new Error("Network response was not ok")

  const reader = response.body?.getReader()
  const decoder = new TextDecoder("utf-8")
  let buffer = ""
  let bufferObj: Record<string, any>
  let chunkBuffer = ""
  let lastUpdate = Date.now()

  function read() {
    let hasError = false
    let isDone = false // 后端返回的 done 标志
    reader
      ?.read()
      .then((result: any) => {
        if (result.done) {
          // 最后一次批量更新
          if (chunkBuffer) {
            onData(chunkBuffer, { messageId: "", errorMessage: "", done: true })
            chunkBuffer = ""
          }
          onCompleted && onCompleted()
          return
        }
        buffer = decoder.decode(result.value, { stream: true })
        const lines = buffer.split("\n")
        try {
          buffer = ""
          lines.forEach((message) => {
            if (message.startsWith("data:")) {
              try {
                bufferObj = JSON.parse(message.substring(5))
              } catch (e) {
                buffer += `${message}\n`
                return
              }
              // 检查后端返回的 done 标志
              if (bufferObj.done === true) {
                isDone = true
              }
              // 卡片元数据帧（content_metadata 数组非空，实测 content 为 null）需立即下发，不进文本节流
              const metadataList =
                Array.isArray(bufferObj.content_metadata) && bufferObj.content_metadata.length > 0
                  ? bufferObj.content_metadata
                  : undefined
              if (typeof bufferObj.content !== "undefined" || metadataList) {
                chunkBuffer += bufferObj.content || ""
                const now = Date.now()
                // done 立即发送剩余内容；卡片元数据帧立即下发（避免被节流丢弃）；其余按 80ms 节流合并
                if (isDone || metadataList || now - lastUpdate > 80) {
                  onData(chunkBuffer, {
                    conversationId: bufferObj.conversation_id,
                    messageId: bufferObj.message_id,
                    taskId: bufferObj.task_id,
                    errorMessage: bufferObj.error || "",
                    done: isDone,
                    contentMetadata: metadataList
                  })
                  chunkBuffer = ""
                  lastUpdate = now
                }
              }
            } else if (message.trim() !== "") {
              buffer += `${message}\n`
            }
          })
          // 如果后端返回 done: true，触发完成回调
          if (isDone) {
            onCompleted && onCompleted()
            return
          }
        } catch (e) {
          console.error("解析流数据失败:", e)
          onError?.(String(e))
          hasError = true
          onCompleted?.(true)
          return
        }
        if (!hasError) setTimeout(read, 0)
      })
      .catch((error) => {
        // 处理中断请求
        if (error.name === "AbortError") {
          console.log("请求已中断")
          onCompleted && onCompleted(true)
          return
        }
        console.error("读取流数据失败:", error)
        onError?.(String(error))
        hasError = true
        onCompleted?.(true)
      })
  }
  read()
}

// 流式POST请求
export const ssePost = (
  url: string,
  fetchOptions: any,
  {
    onData,
    onCompleted,
    onError,
    getAbortController
  }: {
    onData?: IOnData
    onCompleted?: IOnCompleted
    onError?: IOnError
    getAbortController?: (abortController: AbortController) => void
  } = {}
) => {
  const options = Object.assign(
    {},
    baseOptions,
    {
      method: "POST"
    },
    fetchOptions
  )

  // 自动添加token到headers
  const userStore = useUserStore()
  // 根据当前平台获取对应的 token
  const platform = getCurrentPlatform()
  const loginInfo = userStore.getLoginInfoByPlatform()
  const token = loginInfo?.token

  // 设置 headers（需要正确处理 Headers 实例和普通对象）
  if (!options.headers) options.headers = {}
  const setHeader = (key: string, value: string) => {
    if (options.headers instanceof Headers) {
      options.headers.set(key, value)
    } else {
      options.headers[key] = value
    }
  }
  setHeader("Platform", platform)
  if (token) {
    setHeader("Authorization", `jl_ai:${token}`)
  }

  const urlWithPrefix = `${API_PREFIX}${url}`

  const { body } = options
  if (body) options.body = JSON.stringify(body)

  let abortController: AbortController | undefined = undefined
  if (fetchOptions.signal) {
    options.signal = fetchOptions.signal
  } else {
    abortController = new AbortController()
    options.signal = abortController.signal
    if (typeof getAbortController === "function") {
      getAbortController(abortController)
    }
  }

  fetch(urlWithPrefix, options)
    .then((res: any) => {
      const contentType = res.headers.get("Content-Type") || ""
      if (!/^(2|3)\d{2}$/.test(res.status)) {
        res.json().then((data: any) => {
          if (data && (data.ret_code === "A0230" || data.ret_code === "A0400")) {
            handleLoginExpired(data.ret_msg)
            onError?.(data.ret_msg || "登录已过期")
            return
          }
          console.error("SSE请求错误:", data.message || "服务器错误")
          onError?.(data.message || "服务器错误")
        })
        return
      }
      if (contentType.includes("text/event-stream")) {
        return handleStream(
          res,
          (str: string, moreInfo: IOnDataMoreInfo) => {
            onData?.(str, moreInfo)
          },
          () => {
            onCompleted?.()
          },
          (msg) => {
            onError?.(msg)
          }
        )
      } else {
        res
          .json()
          .then((data: any) => {
            if (data && (data.ret_code === "A0230" || data.ret_code === "A0400")) {
              handleLoginExpired(data.ret_msg)
              onError?.(data.ret_msg || "登录已过期")
              return
            }
            onError?.("error")
          })
          .catch((err: any) => {
            onError?.(String(err))
          })
      }
    })
    .catch((e) => {
      if (e.name !== "AbortError") {
        console.error("SSE连接错误:", e)
      }
      onError?.(String(e))
    })
}

// 使用axios的标准请求方法
export const request = (url: string, options = {}) => {
  const { method = "GET", body, params } = options as any

  if (method === "GET") return apiClient.get(url, { params })
  else if (method === "POST") return apiClient.post(url, body)
  else if (method === "PUT") return apiClient.put(url, body)
  else if (method === "DELETE") return apiClient.delete(url, { data: body })

  return Promise.reject(new Error(`不支持的请求方法: ${method}`))
}

// 标准GET请求
export const get = (url: string, options = {}) => {
  return apiClient.get(url, options)
}

// 标准POST请求
export const post = (url: string, options = {}) => {
  const { body } = options as any
  return apiClient.post(url, body)
}

// 标准PUT请求
export const put = (url: string, options = {}) => {
  const { body } = options as any
  return apiClient.put(url, body)
}

// 标准DELETE请求
export const del = (url: string, options = {}) => {
  return apiClient.delete(url, options)
}
